<script setup lang="ts">
import { computed, inject, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { financialSummariesApi } from '@/api/financial-summaries';
import { formatCurrency } from '@/utils/currency';
import type { SerializedUser } from '@/api/users';
import FinancesLineChart from './finances-line-chart.vue';

const props = defineProps<{
  queryParams: string;
  selectedMonthText: string;
}>();

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const { data: financesData } = useQuery({
  queryKey: computed(() => ['financesSummary', props.queryParams]),
  queryFn: () => financialSummariesApi.index(props.queryParams),
});

const currentMonthData = computed(() => {
  if (!financesData.value?.monthlyStats || financesData.value.monthlyStats.length === 0) {
    return null;
  }

  return financesData.value.monthlyStats[financesData.value.monthlyStats.length - 1];
});

const hasMonthlyStats = computed(() =>
  financesData.value?.monthlyStats && financesData.value.monthlyStats.length > 0,
);

const showCheckInFilterInfo = computed(() =>
  currentUser?.value?.companyIncludeCheckedInShiftsOnly,
);
</script>

<template>
  <v-card elevation="2">
    <v-card-title class="d-flex align-center justify-space-between">
      <span class="text-h6 font-weight-bold">Resumen Financiero</span>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col
          cols="12"
          md="3"
        >
          <v-card-text class="py-2">
            <div class="mb-2 text-h6 font-weight-bold">
              {{ selectedMonthText }}
            </div>
            <v-row>
              <v-col
                cols="12"
                class="py-1"
              >
                <v-card
                  variant="outlined"
                  color="success"
                >
                  <v-card-text class="text-center pa-2">
                    <div class="text-subtitle-2 text-success">
                      Ingreso Total
                    </div>
                    <div class="text-h6 font-weight-bold">
                      {{ currentMonthData ? formatCurrency(currentMonthData.expectedIncome.toString()) : '-' }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                class="py-1"
              >
                <v-card
                  variant="outlined"
                  color="error"
                >
                  <v-card-text class="text-center pa-2">
                    <div class="text-subtitle-2 text-error">
                      Costo Bruto Total
                    </div>
                    <div class="text-h6 font-weight-bold">
                      {{ currentMonthData ? formatCurrency(currentMonthData.expectedCost.toString()) : '-' }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                class="py-1"
              >
                <v-card
                  variant="outlined"
                  color="primary"
                >
                  <v-card-text class="text-center pa-2">
                    <div class="text-subtitle-2 text-primary">
                      Margen Bruto $
                    </div>
                    <div class="text-h6 font-weight-bold">
                      {{ currentMonthData ? formatCurrency(currentMonthData.profit.toString()) : '-' }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                class="py-1"
              >
                <v-card
                  variant="outlined"
                  color="info"
                >
                  <v-card-text class="text-center pa-2">
                    <div class="text-subtitle-2 text-info">
                      Margen Bruto %
                    </div>
                    <div class="text-h6 font-weight-bold">
                      {{ currentMonthData?.margin ?
                        `${currentMonthData.margin}%` : '-' }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-col>
        <v-col
          cols="12"
          md="9"
        >
          <v-card-text class="py-2">
            <finances-line-chart
              v-if="hasMonthlyStats"
              :monthly-stats="financesData!.monthlyStats"
            />
            <div
              v-else
              class="text-center pa-8"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="50"
              />
              <p class="mt-4 text-grey">
                Cargando datos financieros...
              </p>
            </div>
          </v-card-text>
        </v-col>
      </v-row>
    </v-card-text>
    <v-alert
      v-if="showCheckInFilterInfo"
      type="info"
      variant="tonal"
      density="compact"
      class="mx-8 mb-8"
    >
      Los meses pasados utilizan sólo los turnos efectivamente marcados para calcular el ingreso y el costo.
      Para el mes actual y meses futuros, se utilizan todos los turnos asignados.
    </v-alert>
  </v-card>
</template>
